import React from 'react';
import AddOutlined from '@mui/icons-material/AddOutlined';
import EditOutlined from '@mui/icons-material/EditOutlined';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { IconButton } from '../../../../../components/ui';
import { useFeatureTheme } from '../../../../../theme/hooks/useFeatureTheme';
import {
  formatFieldOptionsSummary,
  normalizeFieldOptions,
} from '../../utils/fieldOptionsUtils';

function getReadableChipTextColor(color) {
  if (!color || !/^#[0-9A-Fa-f]{6}$/.test(color)) {
    return undefined;
  }
  const red = parseInt(color.slice(1, 3), 16);
  const green = parseInt(color.slice(3, 5), 16);
  const blue = parseInt(color.slice(5, 7), 16);
  const luminance = (0.299 * red + 0.587 * green + 0.114 * blue) / 255;
  return luminance > 0.62 ? '#1f2937' : '#ffffff';
}

function OptionChip({ label, color, radiusPx }) {
  return (
    <Box
      component="span"
      sx={{
        px: 1,
        py: 0.25,
        borderRadius: radiusPx.sm,
        fontSize: 12,
        fontWeight: 500,
        lineHeight: 1.4,
        bgcolor: color || 'action.hover',
        color: getReadableChipTextColor(color) || 'text.secondary',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Box>
  );
}

function OptionsActionButton({ onEdit, label, isAdd, semantic }) {
  if (!onEdit) {
    return null;
  }

  const Icon = isAdd ? AddOutlined : EditOutlined;

  return (
    <Tooltip title={label}>
      <span>
        <IconButton
          size="sm"
          variant="ghost"
          aria-label={label}
          onClick={onEdit}
          sx={{
            color: semantic.interactive.primary,
            '&:hover:not(:disabled)': {
              bgcolor: semantic.interactive.primarySoft,
            },
          }}
        >
          <Icon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  );
}

export default function FieldOptionsPreview({
  keyOptions = [],
  optionColors = {},
  maxVisible = 6,
  variant = 'chips',
  onEdit,
  emptyLabel = 'No options configured',
  addLabel = 'Add options',
  editLabel = 'Edit options',
}) {
  const { semantic, radiusPx, typographyStyles } = useFeatureTheme();
  const options = normalizeFieldOptions(keyOptions);

  const renderChipRow = (visible, remaining, sx = {}) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 0.75,
        ...sx,
        mb: '4px'
      }}
    >
      {visible.map((option) => (
        <OptionChip
          key={option}
          label={option}
          color={optionColors?.[option]}
          radiusPx={radiusPx}
        />
      ))}
      {remaining > 0 ? (
        <Typography variant="caption" color="text.secondary">
          +{remaining} more
        </Typography>
      ) : null}
    </Box>
  );

  if (variant === 'compact') {
    if (!options.length) {
      return null;
    }

    const visible = options.slice(0, maxVisible);
    const remaining = options.length - visible.length;
    return renderChipRow(visible, remaining, { mt: 0.5 });
  }

  if (variant === 'embedded') {
    if (!options.length) {
      return (
        <Typography
          sx={{
            ...typographyStyles.caption,
            color: semantic.text.secondary,
            fontStyle: 'italic',
          }}
        >
          {emptyLabel}
        </Typography>
      );
    }

    const visible = options.slice(0, maxVisible);
    const remaining = options.length - visible.length;
    return renderChipRow(visible, remaining);
  }

  if (variant === 'text') {
    const summary = formatFieldOptionsSummary(options, maxVisible);
    if (!summary) {
      return (
        <Typography
          sx={{
            ...typographyStyles.caption,
            color: semantic.text.secondary,
            mt: 0.25,
            fontStyle: 'italic',
          }}
        >
          {emptyLabel}
        </Typography>
      );
    }

    return (
      <Typography
        sx={{
          ...typographyStyles.caption,
          color: semantic.text.secondary,
          mt: 0.25,
        }}
      >
        {summary}
      </Typography>
    );
  }

  const visible = options.slice(0, maxVisible);
  const remaining = options.length - visible.length;

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: radiusPx.md,
        border: `1px solid ${semantic.borders.border}`,
        bgcolor: semantic.surfaces.surfaceSubtle ?? semantic.surfaces.background,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2">
          {options.length > 0 ? `Options (${options.length})` : 'Options'}
        </Typography>
        <OptionsActionButton
          onEdit={onEdit}
          label={options.length > 0 ? editLabel : addLabel}
          isAdd={options.length === 0}
          semantic={semantic}
        />
      </Stack>

      {options.length > 0 ? (
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75 }}>
          {visible.map((option) => (
            <OptionChip
              key={option}
              label={option}
              color={optionColors?.[option]}
              radiusPx={radiusPx}
            />
          ))}
          {remaining > 0 ? (
            <Typography variant="caption" color="text.secondary">
              +{remaining} more
            </Typography>
          ) : null}
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {emptyLabel}
        </Typography>
      )}
    </Box>
  );
}
